// Landing.jsx
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import ModalView from '../container/ModalView.js';
import MailTo from '../container/MailTo.js';

import FadeOut from '../style/FadeOut.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import FadeInFromBottom from '../style/FadeInFromBottom.js';
import LogoSwagSrc from '../../img/login-swag-icon.png';
import LogoSwagSrc2x from '../../img/login-swag-icon@2x.png';
import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { getIsInIframe } from '../resource/getJsEnvironment.js';

import trackMixpanel, {
  EventTypes,
  ButtonId,
  ModalId,
  getViewId,
} from '../resource/mixpanel.js';
import media from '../style/media.js';

import cnIcon from '../../img/ic-online-service-cn.svg';
import twIcon from '../../img/ic-online-service-tw.svg';
import esIcon from '../../img/ic-online-service-es.svg';
import jpIcon from '../../img/ic-online-service-jp.svg';
import koIcon from '../../img/ic-online-service-ko.svg';
import enIcon from '../../img/ic-online-service-en.svg';

const LogoSrc = typeof LogoSwagSrc === 'object' ? LogoSwagSrc.src : LogoSwagSrc;
const LogoSrc2x =
  typeof LogoSwagSrc2x === 'object' ? LogoSwagSrc2x.src : LogoSwagSrc2x;
const getLngIcon = lng => {
  if (lng === 'zh-CN') {
    return cnIcon;
  } else if (lng === 'zh-TW') {
    return twIcon;
  } else if (lng.includes('es')) {
    return esIcon;
  } else if (lng === 'ja') {
    return jpIcon;
  } else if (lng === 'ko') {
    return koIcon;
  } else {
    return enIcon;
  }
};

const isInIframe = getIsInIframe();

const maxValidVerifiedTime = 10 * 60 * 1000; // 10 minutes

export class Landing extends Component {
  state = { shouldFadeInIntercom: false };
  timeout = null;

  componentDidMount() {
    this.handleModal();
  }
  componentDidUpdate({
    canDisplayIntercom: prevCanDisplayIntercom,
    token: prevToken,
    isPresenceClientRemoteConfigMerged: prevMerged,
  }) {
    const { canDisplayIntercom, token, isPresenceClientRemoteConfigMerged } =
      this.props;
    if (
      (token && token !== !prevToken) ||
      (isPresenceClientRemoteConfigMerged &&
        isPresenceClientRemoteConfigMerged !== prevMerged)
    ) {
      this.handleModal();
    }
    if (canDisplayIntercom && canDisplayIntercom !== prevCanDisplayIntercom) {
      this.setState({ shouldFadeInIntercom: true });
      this.timeout = setTimeout(
        () => this.setState({ shouldFadeInIntercom: false }),
        600
      );
    }
  }
  componentWillUnmount() {
    this.handleClose();
    clearTimeout(this.timeout);
  }
  handleModal() {
    const {
      token,
      hasAgeVerified,
      userCountry,
      needLandingCountries,
      isDisplaying,
      lastAgeVerifiedTimestamp,
      isPresenceClientRemoteConfigMerged,
    } = this.props;

    const hasRecentlyVerified =
      Date.now() - lastAgeVerifiedTimestamp < maxValidVerifiedTime;

    const shouldDisplay =
      !token &&
      !hasAgeVerified &&
      isPresenceClientRemoteConfigMerged &&
      needLandingCountries.includes(userCountry) &&
      !hasRecentlyVerified &&
      !isInIframe;

    if (shouldDisplay && !isDisplaying) {
      this.handleOpen();
    } else if (isDisplaying && !shouldDisplay) {
      this.handleClose();
    }
  }
  handleOpen() {
    this.props.open();
    document.body.style.overflow = 'hidden';

    trackMixpanel({
      type: EventTypes.VIEW_OPENED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        modalId: ModalId.Landing.LandingPage,
      },
    });
  }
  handleClose() {
    if (window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    document.body.style.overflow = 'auto';
    this.props.close();
  }
  handleConfirmClick() {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        modalId: ModalId.Landing.LandingPage,
        buttonId: ButtonId.All.ButtonEnterSWAG,
      },
    });
    this.handleClose();
  }
  handleExitOnClick = () => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        modalId: ModalId.Landing.LandingPage,
        buttonId: ButtonId.All.ButtonExitSWAG,
      },
    });
  };
  renderLandingContent = () => {
    const { shouldFadeInIntercom } = this.state;
    const {
      canDisplayIntercom,
      t,
      i18n,
      status,
      landingBlockerUrl,
      underAgeExitUrl,
    } = this.props;
    return (
      <StyledLanding status={status}>
        <Container>
          <Logo
            srcSet={`
              ${LogoSrc} 1x,
              ${LogoSrc2x} 2x
            `}
            src={LogoSrc}
            alt="SWAG Logo"
          />
          <Notice> {t('age_verify_title')}</Notice>
          <ReportButton>{t('appeal')}</ReportButton>
          <AdultOnlyIcon src={landingBlockerUrl} alt="Adult only" />
          <ButtonSection>
            <ConfirmButton
              onClick={e => {
                e.preventDefault();
                this.handleConfirmClick();
              }}
            >
              {t('landing_signup_for_free')}
            </ConfirmButton>
            <ExitLink
              onClick={() => {
                this.handleExitOnClick();
              }}
              href={underAgeExitUrl}
              rel="noreferrer noopener"
              title="exit"
            >
              {t('landing_exit')}
            </ExitLink>
          </ButtonSection>
        </Container>
        <CustomerServiceWrapper canDisplay={canDisplayIntercom}>
          <CustomerServiceButton
            id="intercom_launcher"
            isIntercomFallback={true}
            shouldFadeIn={shouldFadeInIntercom}
            isIphoneXSeriesPWA={getIsIphoneXSeriesPWA()}
            isDisplayingAgeVerify={true}
          >
            <CustomerServiceIcon
              src={getLngIcon(i18n.language)}
              width="64"
              height="64"
            />
          </CustomerServiceButton>
        </CustomerServiceWrapper>
      </StyledLanding>
    );
  };
  render() {
    return (
      <ModalView id="LandingPageModal" render={this.renderLandingContent} />
    );
  }
}

Landing.propTypes = {
  t: PropTypes.func.isRequired,
  token: PropTypes.string,
  hasAgeVerified: PropTypes.bool,
  lastAgeVerifiedTimestamp: PropTypes.number,
  userCountry: PropTypes.string,
  needLandingCountries: PropTypes.array,
  landingBlockerUrl: PropTypes.string,
  underAgeExitUrl: PropTypes.string,
  isDisplaying: PropTypes.bool,
  canDisplayIntercom: PropTypes.bool,
  i18n: PropTypes.object,
  status: PropTypes.string,
  open: PropTypes.func,
  close: PropTypes.func,
  isPresenceClientRemoteConfigMerged: PropTypes.bool,
};

Landing.defaultProps = {
  token: '',
  hasAgeVerified: false,
  lastAgeVerifiedTimestamp: 0,
  userCountry: '',
  needLandingCountries: [],
  landingBlockerUrl: '',
  underAgeExitUrl: '',
  isDisplaying: false,
  canDisplayIntercom: false,
  i18n: {},
  status: undefined,
  open: () => null,
  close: () => null,
  isPresenceClientRemoteConfigMerged: false,
};

const StyledLanding = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.8);
  animation: 0.6s forwards
    ${({ status }) => ('closing' === status ? FadeOut : null)};
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 400px;
  ${media.mobile`
    width: 260px;
  `};
  min-height: 376px;
  padding: 32px 16px;
  background-color: #ffffff;
  border-radius: 4px;
  text-align: center;
`;

const Logo = styled.img`
  width: 120px;
  height: 52.23px;
`;

const Notice = styled.div`
  font-size: 14px;
  color: #d0021b;
  margin-top: 20px;
  margin-bottom: 12px;
`;

const ReportButton = styled(MailTo)`
  ${ResetButtonStyle};
  padding: 6px;
  border-radius: 4px;
  border: solid 1px #d0021b;
  background-color: #ffffff;
  color: #d0021b;
  font-size: 12px;
  cursor: pointer;
`;

const AdultOnlyIcon = styled.img`
  width: 180px;
  height: 119px;
  margin: 16px auto 24px;
`;

const ConfirmButton = styled.button`
  ${ResetButtonStyle};
  height: 40px;
  width: 110px;
  line-height: 40px;
  border-radius: 4px;
  background-color: #000000;
  font-size: 16px;
  color: #ffffff;
  text-align: center;
  cursor: pointer;
  margin-right: 8px;

  :hover {
    opacity: 0.7;
  }
`;

const ExitLink = styled.a`
  height: 40px;
  width: 110px;
  line-height: 40px;
  border-radius: 4px;
  background-color: #000000;
  font-size: 16px;
  color: #ffffff;
  text-align: center;

  cursor: pointer;
`;

const CustomerServiceWrapper = styled.div`
  ${({ canDisplay }) =>
    !canDisplay
      ? `
        opacity: 0;
        pointer-events: none;
      `
      : ''}
`;

const CustomerServiceButton = styled(MailTo)`
  ${ResetButtonStyle};
  position: fixed;
  right: 16px;
  bottom: ${({ isIphoneXSeriesPWA, isJoin, isDisplayingAgeVerify }) => {
    if (isJoin) return 132;
    return (
      48 + // Footer
      17 + // btn margin
      (isDisplayingAgeVerify ? 88 : 0) +
      (isIphoneXSeriesPWA ? 34 : 0)
    );
  }}px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  cursor: pointer;

  animation: 0.6s forwards
    ${({ shouldFadeIn }) => (shouldFadeIn ? FadeInFromBottom : '')};
`;

const CustomerServiceIcon = styled.img`
  width: 64px;
  height: 64px;
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: row;
`;

export default withTranslation()(Landing);
